import { useEffect, useRef, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Check, DeviceMobile, Desktop } from '@phosphor-icons/react';
import { useWebpages } from '@/contexts/WebpagesContext';
import { useOnboardingStatus } from '@/hooks/useOnboardingStatus';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { RouteLoader } from '@/components/RouteLoader';
import {
  IOSSafariInstall,
  AndroidChromeInstall,
  IOSNonSafariCallout,
} from './components/PWAInstallSteps';
import { analytics } from '@/lib/analytics';
import { consumePendingDestination } from '@/lib/auth-next';

type Platform = 'ios-safari' | 'ios-non-safari' | 'android' | 'other';

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) {
    return /CriOS|FxiOS|EdgiOS/.test(ua) ? 'ios-non-safari' : 'ios-safari';
  }
  if (/Android/.test(ua)) return 'android';
  return 'other';
}

function isPWAInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

async function getToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Not authenticated');
  return token;
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getToken();
  const res = await fetch(path, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return res.json() as Promise<T>;
}

export function OnboardingMobile() {
  const { user, isAuthLoading } = useWebpages();
  const navigate = useNavigate();
  const { status, loading: statusLoading } = useOnboardingStatus(!!user);

  const [platform, setPlatform] = useState<Platform>('other');
  const [pwaInstalled, setPwaInstalled] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const mobileInitCalledRef = useRef(false);
  const firedRef = useRef<Set<string>>(new Set());
  const mountedAtRef = useRef<number>(Date.now());

  useEffect(() => {
    setPlatform(detectPlatform());
    setPwaInstalled(isPWAInstalled());
  }, []);

  useEffect(() => {
    if (statusLoading || !status) return;
    if (firedRef.current.has('viewed')) return;
    firedRef.current.add('viewed');
    analytics.track('onboarding_step_viewed', {
      step_index: 5, step_name: 'mobile_pwa',
      device: 'mobile',
    });
  }, [statusLoading, status]);

  // Fire desktop-setup email once
  useEffect(() => {
    if (!user || mobileInitCalledRef.current) return;
    if (status?.mobile_email_sent_at) {
      mobileInitCalledRef.current = true;
      return;
    }
    mobileInitCalledRef.current = true;
    api('/api/onboarding/mobile-init', { method: 'POST', body: JSON.stringify({}) })
      .catch(() => { mobileInitCalledRef.current = false; });
  }, [user, status?.mobile_email_sent_at]);

  const handleDismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    // Mobile path is "skip the desktop funnel for now" — track it as a skip
    // rather than a completion so the desktop funnel stays clean.
    analytics.track('onboarding_skipped', {
      step_index: 5, step_name: 'mobile_pwa',
      pwa_installed: pwaInstalled,
      duration_ms: Math.max(0, Date.now() - mountedAtRef.current),
    });
    try {
      await api('/api/onboarding/mobile-dismiss', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      // Honor any preserved destination (e.g. /upgrade?from=email_trial_d11)
      // captured by ProtectedRoute before the onboarding bounce.
      const dest = consumePendingDestination() || '/saved';
      navigate(dest, { replace: true });
    } catch (err) {
      console.error('Dismiss failed:', err);
      setDismissing(false);
    }
  };

  if (isAuthLoading || statusLoading) {
    return <RouteLoader />;
  }

  if (status?.completed_at) {
    return <Navigate to="/saved" replace />;
  }

  return (
    <main className="min-h-screen bg-white">
      <Helmet>
        <title>Welcome to Solem</title>
      </Helmet>

      <div className="mx-auto max-w-xl px-5 pt-12 pb-24">
        <header className="mb-8">
          <p className="mb-2 text-sm font-medium uppercase tracking-wider text-neutral-400">
            Welcome to Solem
          </p>
          <h1 className="text-2xl font-light tracking-tight text-neutral-900">
            👋 Hey there.
          </h1>
          <p className="mt-3 text-base font-light leading-relaxed text-neutral-600">
            Solem really shines on the desktop. Most of what makes it powerful —
            one-shortcut saves, AI agent integration — needs a browser extension
            and an MCP-compatible app.
          </p>
          <p className="mt-3 text-base font-light leading-relaxed text-neutral-600">
            But two useful things you can do right now:
          </p>
        </header>

        {/* ── Step 1 — PWA install ───────────────────────────── */}
        <section className="mb-8">
          <div className="mb-4 flex items-center gap-3">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-neutral-900 text-sm font-medium text-white">
              <DeviceMobile size={14} weight="bold" />
            </span>
            <h2 className="text-base font-medium text-neutral-900">
              Install Solem on your home screen
            </h2>
          </div>

          {pwaInstalled ? (
            <div className="flex items-center gap-2 rounded-sm border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-700">
              <Check size={16} weight="bold" className="text-neutral-900" />
              You're using Solem as an installed app — nice.
            </div>
          ) : (
            <div className="space-y-3">
              {platform === 'ios-safari' && <IOSSafariInstall />}
              {platform === 'android' && <AndroidChromeInstall />}
              {platform === 'ios-non-safari' && <IOSNonSafariCallout />}
              {platform === 'other' && (
                <p className="rounded-sm border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-600">
                  Open this page in Safari (iOS) or Chrome (Android) to add Solem
                  to your home screen.
                </p>
              )}
            </div>
          )}
        </section>

        {/* ── Step 2 — Desktop preview ──────────────────────── */}
        <section className="mb-10">
          <div className="mb-4 flex items-center gap-3">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-neutral-900 text-sm font-medium text-white">
              <Desktop size={14} weight="bold" />
            </span>
            <h2 className="text-base font-medium text-neutral-900">
              When you get to a desktop
            </h2>
          </div>
          <div className="rounded-sm border border-neutral-200 bg-neutral-50 px-4 py-4">
            <p className="mb-3 text-sm text-neutral-700">
              Next time you sign in on a laptop or desktop, we'll walk you through
              two quick steps (~2 min total):
            </p>
            <ul className="space-y-2 text-sm text-neutral-700">
              <li className="flex items-start gap-2">
                <span className="mt-0.5 inline-flex h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400" />
                Install the Chrome extension
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 inline-flex h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400" />
                Plug Solem into your AI agent via MCP
              </li>
            </ul>
            <p className="mt-4 text-xs text-neutral-500">
              We've sent the setup guide to your email so it's there when you're ready.
            </p>
          </div>
        </section>

        <Button
          onClick={handleDismiss}
          disabled={dismissing}
          className="w-full rounded-sm"
          size="lg"
        >
          {dismissing ? 'One moment…' : 'Got it — let me explore Solem'}
        </Button>
      </div>
    </main>
  );
}
